import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@avenue-8/sidekick-ui";
import {
  FormControl,
  FormControlLabel,
  ListSubheader,
  MenuItem,
  Select,
  Skeleton,
  Switch,
} from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { ResponsiveSheetDialog } from "@shared/components/ResponsiveSheetDialog";
import type { InsightsSectionConfig } from "@cma/presentation-generation-logic";
import type { PropertyTypeDto } from "@shared/apis/property-search/generated";
import {
  processPropertyTypesForDisplay,
  findPropertyTypeById,
} from "@cma/shared/helpers/dynamic-property-types-helpers";
import { useWidgetsGetTerritorySuggestions } from "@cma/shared/queries/useWidgetsGetTerritorySuggestions";
import {
  type TerritoryItem,
  TerritoryItemTypeEnum,
} from "@shared/apis/sidekick/generated";
import {
  formatTerritoryDisplayName,
  formatTerritoryOptionLabel,
  parseTerritoryName,
} from "@cma/chat-widgets/find-listings-search/components/TerritorySearchBarSuggestions/territory-search-bar-suggestions-helpers";

type DealType = "Sale" | "Rental";

type FilterDraft = {
  neighborhood: TerritoryItem | null;
  dealType: DealType;
  propertyTypeNameId: string;
  propertyTypeDirty: boolean;
  showMedianSalesPriceYearComparision: boolean;
  showAverageLivingSpaceSqftPrice: boolean;
  showInventorySpeedComparison: boolean;
};

const resolvePropertyTypeNameId = (
  propertyTypeRaw: string | undefined,
  propertySubTypeRaw: string | undefined,
  propertyTypes: PropertyTypeDto[],
): string => {
  if (!propertyTypeRaw) return "";

  const parentType = propertyTypes.find((pt) => pt.name === propertyTypeRaw);
  if (!parentType) return "";

  if (!propertySubTypeRaw) return parentType.nameId;

  const subType = parentType.subTypes?.find(
    (st) => st.name === propertySubTypeRaw,
  );
  if (!subType) return "";

  // processPropertyTypesForDisplay uses mapping.nameId for subtypes that have
  // mappings, so we must return a mapping.nameId here to match the dropdown values.
  const firstDisplayedMapping = subType.mappings?.find(
    (m) =>
      (m.connectionType === "Same As" ||
        m.connectionType === "Specialization Of") &&
      m.display !== "Hidden" &&
      m.nameId,
  );
  return firstDisplayedMapping?.nameId ?? subType.nameId;
};

const toFilterDraft = (
  config: InsightsSectionConfig,
  propertyTypes: PropertyTypeDto[],
): FilterDraft => ({
  neighborhood: config.neighborhood
    ? {
        type: TerritoryItemTypeEnum.Neighborhood,
        name: config.neighborhood,
        state: config.state ?? "",
        city: config.city,
      }
    : null,
  dealType: config.isLease ? "Rental" : "Sale",
  propertyTypeNameId: resolvePropertyTypeNameId(
    config.propertyTypeRaw,
    config.propertySubTypeRaw,
    propertyTypes,
  ),
  propertyTypeDirty: false,
  showMedianSalesPriceYearComparision:
    config.showMedianSalesPriceYearComparision ?? true,
  showAverageLivingSpaceSqftPrice:
    config.showAverageLivingSpaceSqftPrice ?? true,
  showInventorySpeedComparison: config.showInventorySpeedComparison ?? true,
});

const resolvePropertyTypeFields = (
  nameId: string,
  propertyTypes: PropertyTypeDto[],
): { propertyTypeRaw?: string; propertySubTypeRaw?: string } => {
  const { parentType, subType } = findPropertyTypeById(nameId, propertyTypes);
  if (!parentType) return {};
  return {
    propertyTypeRaw: parentType.name,
    propertySubTypeRaw: subType?.name,
  };
};

const INSIGHT_TOGGLES: {
  key: keyof FilterDraft & `show${string}`;
  label: string;
}[] = [
  { key: "showInventorySpeedComparison", label: "Inventory Speed" },

  {
    key: "showAverageLivingSpaceSqftPrice",
    label: "Avg. Living Space Price/sqft",
  },
  {
    key: "showMedianSalesPriceYearComparision",
    label: "Median Sales Price Trend",
  },
];

type NeighborhoodSearchFieldProps = {
  value: TerritoryItem | null;
  onChange: (value: TerritoryItem | null) => void;
};

const NeighborhoodSearchField = ({
  value,
  onChange,
}: NeighborhoodSearchFieldProps) => {
  const [inputValue, setInputValue] = useState(
    value ? formatTerritoryDisplayName(value) : "",
  );
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const debouncedSetQuery = useDebouncedCallback((query: string) => {
    setDebouncedQuery(query.length >= 1 ? query : "");
  }, 500);

  useEffect(() => {
    setInputValue(value ? formatTerritoryDisplayName(value) : "");
  }, [value]);

  const { data, isLoading } = useWidgetsGetTerritorySuggestions({
    query: debouncedQuery,
  });

  const options = useMemo(
    () =>
      (data?.suggestions ?? []).filter(
        (s) => s.type === TerritoryItemTypeEnum.Neighborhood,
      ),
    [data],
  );

  const handleChange = (_e: unknown, newValue: TerritoryItem | null) => {
    setInputValue(newValue ? formatTerritoryDisplayName(newValue) : "");
    onChange(newValue);
  };

  const handleInputChange = (_e: unknown, newVal: string, reason: string) => {
    if (reason === "reset") return;
    setInputValue(newVal);
    debouncedSetQuery(newVal);
  };

  return (
    <Autocomplete
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      getOptionLabel={(option) => formatTerritoryDisplayName(option)}
      isOptionEqualToValue={(opt, val) =>
        opt.name === val.name && opt.state === val.state
      }
      filterOptions={(opts) => opts}
      loading={isLoading && debouncedQuery.length > 0}
      noOptionsText={
        debouncedQuery.length === 0
          ? "Start typing to search..."
          : "No neighborhoods found"
      }
      renderOption={({ key: _key, ...rest }, option) => (
        <Box
          component="li"
          key={`${option.name}-${option.state}-${option.city ?? ""}`}
          {...rest}
        >
          {formatTerritoryOptionLabel(option)}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search neighborhoods"
          size="small"
          hideHelperTextSpace
          sx={{
            "& .MuiInputBase-input": { padding: "0.5rem !important" },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && debouncedQuery.length > 0 && (
                  <CircularProgress size={16} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

type Props = {
  open: boolean;
  onClose: () => void;
  sectionConfig: InsightsSectionConfig;
  propertyTypes: PropertyTypeDto[];
  isLoadingPropertyTypes: boolean;
  onApply: (updates: Partial<InsightsSectionConfig>) => void;
};

export const InsightsFilterSheet = ({
  open,
  onClose,
  sectionConfig,
  propertyTypes,
  isLoadingPropertyTypes,
  onApply,
}: Props) => {
  const [draft, setDraft] = useState<FilterDraft>(() =>
    toFilterDraft(sectionConfig, []),
  );

  const mlsDealType = draft.dealType === "Rental" ? "Lease" : "Sale";

  const filteredPropertyTypes = useMemo(
    () => propertyTypes.filter((pt) => pt.dealType === mlsDealType),
    [propertyTypes, mlsDealType],
  );

  const processedTypes = useMemo(
    () => processPropertyTypesForDisplay(filteredPropertyTypes),
    [filteredPropertyTypes],
  );

  // Stable property types derived from sectionConfig (not draft) so that
  // a user changing deal type doesn't re-trigger the reset effect below.
  const configMlsDealType = sectionConfig.isLease ? "Lease" : "Sale";
  const configFilteredPropertyTypes = useMemo(
    () => propertyTypes.filter((pt) => pt.dealType === configMlsDealType),
    [propertyTypes, configMlsDealType],
  );

  // Reset draft when sheet opens or when property types finish loading so the
  // pre-selection can be resolved by name against the tree.
  useEffect(() => {
    if (!open) return;
    setDraft(toFilterDraft(sectionConfig, configFilteredPropertyTypes));
  }, [open, sectionConfig, configFilteredPropertyTypes]);

  const setField = <K extends keyof FilterDraft>(
    key: K,
    value: FilterDraft[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleDealTypeChange = (_: unknown, value: DealType | null) => {
    if (!value) return;
    setDraft((prev) => ({
      ...prev,
      dealType: value,
      propertyTypeNameId: "",
      propertyTypeDirty: true,
    }));
  };

  const resolveSelectedLabel = (value: string): string => {
    for (const group of processedTypes) {
      const sub = group.subTypes.find((s) => s.nameId === value);
      if (sub) return sub.displayName;
      if (group.nameId === value) return group.name;
    }
    return value;
  };

  const handleApply = () => {
    const { propertyTypeRaw, propertySubTypeRaw } = draft.propertyTypeNameId
      ? resolvePropertyTypeFields(
          draft.propertyTypeNameId,
          filteredPropertyTypes,
        )
      : draft.propertyTypeDirty
        ? {}
        : {
            propertyTypeRaw: sectionConfig.propertyTypeRaw,
            propertySubTypeRaw: sectionConfig.propertySubTypeRaw,
          };

    onApply({
      neighborhood: draft.neighborhood
        ? parseTerritoryName(draft.neighborhood.name).baseName
        : undefined,
      city: draft.neighborhood?.city,
      state: draft.neighborhood?.state || undefined,
      isLease:
        draft.dealType === (sectionConfig.isLease ? "Rental" : "Sale")
          ? sectionConfig.isLease
          : draft.dealType === "Rental",
      propertyTypeRaw,
      propertySubTypeRaw,
      showMedianSalesPriceYearComparision:
        draft.showMedianSalesPriceYearComparision,
      showAverageLivingSpaceSqftPrice: draft.showAverageLivingSpaceSqftPrice,
      showInventorySpeedComparison: draft.showInventorySpeedComparison,
    });
    onClose();
  };

  return (
    <ResponsiveSheetDialog
      open={open}
      onClose={onClose}
      title="Market Insights"
      maxWidth="xs"
    >
      <Box display="flex" flexDirection="column" gap={2.5}>
        <FormControl fullWidth>
          <FormLabel>Neighborhood</FormLabel>
          <NeighborhoodSearchField
            value={draft.neighborhood}
            onChange={(value) => setField("neighborhood", value)}
          />
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.75 }}>Deal Type</FormLabel>
          <ToggleButtonGroup
            exclusive
            value={draft.dealType}
            onChange={handleDealTypeChange}
            size="small"
          >
            <ToggleButton value="Sale">Sale</ToggleButton>
            <ToggleButton value="Rental">Rental</ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <FormControl fullWidth>
          <FormLabel sx={{ mb: 0.75 }}>Property Type</FormLabel>
          {isLoadingPropertyTypes ? (
            <Skeleton variant="rectangular" height={40} />
          ) : (
            <Select
              value={draft.propertyTypeNameId}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  propertyTypeNameId: e.target.value,
                  propertyTypeDirty: true,
                }))
              }
              displayEmpty
              renderValue={(value) =>
                value ? resolveSelectedLabel(value) : <em>Any</em>
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
            >
              <MenuItem value="">
                <em>Any</em>
              </MenuItem>
              {processedTypes.flatMap((group) =>
                group.subTypes.length > 0
                  ? [
                      <ListSubheader key={`hdr-${group.nameId}`}>
                        {group.name}
                      </ListSubheader>,
                      ...group.subTypes.map((sub) => (
                        <MenuItem key={sub.nameId} value={sub.nameId}>
                          {sub.displayName}
                        </MenuItem>
                      )),
                    ]
                  : [
                      <MenuItem key={group.nameId} value={group.nameId}>
                        {group.name}
                      </MenuItem>,
                    ],
              )}
            </Select>
          )}
        </FormControl>

        <Divider />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            Show insights
          </Typography>
          {INSIGHT_TOGGLES.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={draft[key] as boolean}
                  onChange={(e) => setField(key, e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{label}</Typography>}
              labelPlacement="start"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                ml: 0,
                width: "100%",
                mb: 1,
              }}
            />
          ))}
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1} mt={0.5}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
        </Box>
      </Box>
    </ResponsiveSheetDialog>
  );
};
